import { normalizeContainerNo } from './packingListAllocation';
import { newDraftLine } from './packingListDraft';
import { ensureQuantities, primaryQuantity } from './packingListQuantity';
import { PL_COMPONENT } from './packingListRequest';

/** 팀원이 문서·배분을 바꿨다는 알림. 기존 update 이벤트의 reducer 자리에 실려 온다. */
export const PL_EVENT_CHANGED = 'packing_list:changed';
/** 파싱 결과가 DB에 반영됐다는 알림. */
export const PL_EVENT_PARSED = 'packing_list:parsed';

/** Packing List를 올리는 운송 유형: 해상 수입·항공 수입. 서버도 나머지 유형은 거절한다. */
export const PL_SHIPMENT_TYPE_BY_TAB = { 0: 'il_ocean_import', 2: 'il_air_import' };
export const isPackingListTab = (tab) => Object.prototype.hasOwnProperty.call(PL_SHIPMENT_TYPE_BY_TAB, tab);

/** House 한 건의 화면 상태 키. 같은 번호의 House가 해상·항공에 따로 있어 운송 유형과 함께 쓴다. */
export const workspaceKey = (tab, houseId) => `${PL_SHIPMENT_TYPE_BY_TAB[tab] ?? tab}:${houseId}`;
export const workspaceKeyFromScope = (scope) => `${scope?.shipment_type}:${scope?.house_id}`;

/**
 * 서버 update 이벤트([tab, format_, component, reducer, data]) 중 Packing List 알림인지.
 * 기존 handleSocketUpdate는 reducer 이름으로 슬라이스를 찾는데 PL 알림은 그 목록에 없으므로 먼저 가려낸다.
 */
export const isPackingListEvent = (received) =>
    Array.isArray(received) && received[2] === PL_COMPONENT && [PL_EVENT_CHANGED, PL_EVENT_PARSED].includes(received[3]);

/** 배분 대상 종류. 해상은 컨테이너, 항공은 HBL Alias에 싣는다. 서버가 종류에 맞는 실제 테이블을 검증한다. */
export const allocationTargetKind = (tab) => (tab === 2 ? 'air_alias' : 'container');

/** 배분 변경 요청 한 품목. 품목별 entries 전체 치환이라 0 이하 몫은 빼고 보낸다(빈 배열 = 전체 해제). */
export const toAllocationRequest = (itemId, entries, targetKind = 'container') => ({
    item_id: itemId,
    entries: entries
        .filter((e) => Number(e.alloc_qty) > 0)
        .map((e) => ({ target_kind: targetKind, target_id: e.container_id, alloc_qty: Number(e.alloc_qty) })),
});

export const DOC_STATUS = { DRAFT: 'draft', CONFIRMED: 'confirmed' };
export const isConfirmedDoc = (doc) => doc?.status === DOC_STATUS.CONFIRMED;
/** 검수 화면에 연 문서가 확정본 수정인지(toEditDocument로 만든 화면 전용 문서). */
export const isEditDraft = (doc) => Boolean(doc?.editing);

/** 확정 품목 한 줄을 수정 화면의 라인으로 편다. 라인은 고치는 확정 품목 id(item_id)를 싣는다. */
const itemToEditLine = (item) => {
    const { id, il_packing_list_id: _doc, il_trade_partner_id_client: _client, item_key: _key, allocated_qty: _allocated, remaining_qty: _remaining, qty: _qty, qty_uom: _uom, po_no, quantities = [], ...fields } = item;
    return ensureQuantities(newDraftLine({ ...fields, item_id: id, po_number: po_no ?? '', quantities: quantities.map(({ qty, uom, uom_raw, is_primary }) => ({ qty, uom, uom_raw, is_primary })) }));
};

/**
 * 확정본 수정 화면에 여는 문서. 서버에 수정 초안을 만들지 않는다 — 헤더·PL 하단 컨테이너 번호는 확정본 문서(read/document)에서,
 * 라인은 그 PL의 확정 품목에서 가져와 화면에서만 고친다. 창을 닫으면 아무것도 저장하지 않고, 재확정(confirm_edit)할 때만 서버에 보낸다.
 * baseAllocationVersion은 수정을 연 시점의 배분 버전이다. 그 사이 배분이 바뀌었으면 서버가 재확정을 거절한다.
 */
export const toEditDocument = (data, items, baseAllocationVersion) => {
    const confirmed = fromDocument(data);
    const lines = [...items].sort((a, b) => (a.line_no ?? 0) - (b.line_no ?? 0)).map((item) => toWireLine(itemToEditLine(item)));
    return { ...confirmed, editing: true, baseAllocationVersion, payload: { ...confirmed.payload, lines } };
};

/** 서버 영향 요약(read/edit_impact) 응답을 다이얼로그 모양으로 바꾼다. issues는 서버 검증 결과로, 있으면 재확정할 수 없다. */
export const fromEditImpact = (data) => ({
    kept: data?.kept ?? 0,
    auto: data?.auto ?? 0,
    unallocated: data?.unallocated ?? 0,
    deleted: data?.deleted ?? 0,
    added: data?.added ?? 0,
    shipperData: data?.shipper_data ?? 0,
    issues: data?.issues ?? [],
});

/** 확정본 삭제 영향(read/delete_impact) 응답을 확인창 모양으로 바꾼다. 함께 지워지는 라인·배분·화주 데이터 건수다. */
export const fromDeleteImpact = (data) => ({
    items: Number(data?.item_count ?? 0),
    allocations: Number(data?.allocation_count ?? 0),
    shipperData: Number(data?.shipper_data_count ?? 0),
});

/** 확정이 같은 Invoice 때문에 멈췄다는 거절. 담당자가 새 PL로 둘지, 기존 확정본을 교체할지 골라 다시 보내야 한다. */
export const DUPLICATE_INVOICE_CODE = 'DUPLICATE_INVOICE';

/** 같은 Invoice 거절(409 DUPLICATE_INVOICE)의 교체 후보. 교체를 고르면 후보의 id·version을 그대로 돌려보낸다. */
export const fromDuplicateCandidates = (data) =>
    (data?.candidates ?? []).map((c) => ({
        id: c.id,
        version: c.version,
        invoiceNo: c.invoice_no ?? null,
        filename: c.original_filename ?? null,
        confirmedAt: c.confirmed_at ?? null,
        itemCount: Number(c.item_count ?? 0),
    }));

/** 같은 Invoice 확정 선택을 confirm 본문에 싣는 모양. create_new는 새 PL로, replace는 고른 확정본의 라인을 이 문서로 바꾼다. */
export const toDuplicateChoice = (choice) =>
    choice.action === 'replace' ? { duplicate_action: 'replace', replace_target: { id: choice.target.id, version: choice.target.version } } : { duplicate_action: 'create_new' };

/** 파서가 아직 결과를 내지 않은 상태. 이 동안은 검수 대신 원본과 진행 안내를 보여준다. */
export const PARSING_STATUSES = ['queued', 'parsing'];
/** 연 문서가 파싱 대기 중인지. */
export const isParsingDoc = (doc) => PARSING_STATUSES.includes(doc?.upload_status);
/** 파싱을 끝내 포기해 편집할 초안이 없는 미확정 문서. 검수를 열 수 없다. 작업 정보 키가 아예 없는 응답은 모르는 것이라 해당하지 않는다. */
export const isClosedDoc = (doc) => doc?.status !== DOC_STATUS.CONFIRMED && doc?.upload_status === null;
/** 파싱 결과 알림인지. 문서 version이 그대로여도 새 소식이다. */
export const isParsedEvent = (event) => event?.parse_status != null || event?.parse_result != null;

/**
 * 카드 한 장의 상태. parsing은 파서가 읽는 중, parse_failed는 빈 초안으로 열려 수기 입력·다시 파싱이 가능한 문서,
 * failed는 파싱을 끝내 포기해 지우고 다시 올려야 하는 문서다. 작업 정보 키가 아예 없는 응답은 draft로 둔다.
 */
export const DOC_CARD_STATE = { DRAFT: 'draft', CONFIRMED: 'confirmed', PARSING: 'parsing', PARSE_FAILED: 'parse_failed', FAILED: 'failed' };
export const docCardState = (doc) => {
    if (isConfirmedDoc(doc)) return DOC_CARD_STATE.CONFIRMED;
    const upload = doc?.open_upload;
    if (upload === null) return DOC_CARD_STATE.FAILED;
    if (PARSING_STATUSES.includes(upload?.status)) return DOC_CARD_STATE.PARSING;
    if (upload?.parse_result === 'failed') return DOC_CARD_STATE.PARSE_FAILED;
    return DOC_CARD_STATE.DRAFT;
};

/** 서버 확정 품목 한 줄을 아이템 표의 한 줄로 편다. Qty·UOM 칸에는 대표 수량(is_primary)을 보여준다. */
const toItem = (item) => {
    const quantities = item.quantities ?? [];
    const primary = primaryQuantity(quantities);
    return { ...item, packing_list_id: item.il_packing_list_id, po_number: item.po_no ?? '', quantities, qty: primary?.qty ?? null, qty_uom: primary?.uom ?? '' };
};

/**
 * 작업 영역(read/workspace) 응답을 화면 모양으로 바꾼다. 서버 필드명이 바뀌면 여기만 고친다.
 * allocations는 품목 id → [{ container_id, alloc_qty, allocation_source }]다. 항공은 container_id 자리에 Alias id가 온다.
 * documentHints는 문서 id → PL 하단에 적힌 컨테이너 번호 목록이다.
 */
export const fromWorkspace = (data) => {
    const allocations = {};
    (data?.allocations ?? []).forEach((row) => {
        if (row?.entries?.length) {
            allocations[row.item_id] = row.entries.map((a) => ({ container_id: a.target_id ?? a.container_id, alloc_qty: a.alloc_qty, allocation_source: a.allocation_source }));
        }
    });
    const documents = data?.documents ?? [];
    return {
        house: data?.house ?? null,
        documents,
        items: (data?.items ?? []).map(toItem),
        allocations,
        allocationVersion: data?.allocation_version ?? 0,
        documentHints: Object.fromEntries(documents.filter((d) => d.container_numbers_in_doc?.length).map((d) => [d.id, d.container_numbers_in_doc])),
    };
};

/**
 * 화면 헤더 칸 → draft_payload 헤더 키. 칸 이름이 il_packing_list 컬럼과 다른 것만 적는다.
 * 나머지 칸(invoice_no, total_gross_weight …)은 이름 그대로 싣는다.
 */
const HEADER_WIRE_KEYS = { po_number: 'po_no' };
const LINE_WIRE_KEYS = { po_number: 'po_no' };

/** 헤더 칸의 파서 신뢰도. 손으로 고친 칸은 null. 서버 키(po_no)와 화면 칸(po_number) 차이를 여기서 맞춘다. */
export const headerConfidence = (header, field) => {
    if ((header?.edited_fields ?? []).includes(field)) return null;
    const score = header?.field_confidence?.[HEADER_WIRE_KEYS[field] ?? field];
    return typeof score === 'number' ? score : null;
};

/** 헤더 칸을 손으로 고쳤다고 기록한다. 편집본과 함께 저장돼 다시 열어도 신뢰도 점이 돌아오지 않는다. */
export const markHeaderEdited = (header, field) => {
    const edited = header?.edited_fields ?? [];
    return edited.includes(field) ? header : { ...header, edited_fields: [...edited, field] };
};

const renameKeys = (obj, map) => Object.fromEntries(Object.entries(obj ?? {}).map(([k, v]) => [map[k] ?? k, v]));
const invert = (map) => Object.fromEntries(Object.entries(map).map(([k, v]) => [v, k]));

/** 라인의 qty·qty_uom은 대표 수량을 비춘 화면용 칸이라 싣지 않는다. 수량은 quantities 한 곳에만 둔다. */
const LINE_SCREEN_ONLY = ['qty', 'qty_uom', 'qty_uom_raw'];

const trimmed = (value) => String(value ?? '').trim();

/**
 * 수량 한 줄의 draft_payload 모양: { qty, uom, uom_raw, is_primary }. 라인마다 대표(is_primary) 한 줄.
 * - 대표 수량: uom은 UOM 칸에서 고른 마스터 코드(미인식이면 적힌 그대로), uom_raw는 파서가 읽은 원문(없으면 null).
 * - 보조 수량: Other Qty에 PL에 적힌 그대로 친 단위. 마스터와 대조하지 않고 uom·uom_raw에 같은 글자를 싣는다(앞뒤 공백만 걷음, 대소문자 유지).
 *   서버는 uom_raw를 il_pl_item_quantity.uom_raw에 그대로 옮기고, 마스터에 없는 단위라도 거절하지 않는다.
 */
const toWireQuantity = ({ qty, uom, uom_raw, is_primary }) => {
    const written = trimmed(uom);
    const raw = trimmed(uom_raw) || (is_primary ? '' : written);
    return { qty, uom: written, uom_raw: raw || null, is_primary: Boolean(is_primary) };
};

const toWireLine = (line) => {
    const { quantities } = ensureQuantities(line);
    const rest = Object.fromEntries(Object.entries(line).filter(([k]) => !LINE_SCREEN_ONLY.includes(k) && k !== 'quantities'));
    return {
        ...renameKeys(rest, LINE_WIRE_KEYS),
        quantities: quantities.map(toWireQuantity),
    };
};

/**
 * 검수 화면에서 고친 헤더·라인을 Save Draft·Confirm에 싣는다. 화면이 고치지 않는 값(raw_document 등)은 base 그대로 둔다.
 * PL 상단·하단 컨테이너 번호는 화면에서 고칠 수 없으므로 비워서 싣는다. 컨테이너 번호는 라인 칸에서만 B/L과 대조한다.
 */
export const toDraftPayload = ({ header, lines, totals, base = null }) => ({
    ...(base ?? {}),
    header: renameKeys(header, HEADER_WIRE_KEYS),
    lines: lines.map((line, index) => toWireLine({ ...line, line_no: index + 1 })),
    container_numbers_in_doc: [],
    // 문서 합계행(파서가 읽은 totals). 검수자가 보조 합계(팔레트 등)를 고치면 여기 실린다. 주지 않으면 base의 것을 그대로 둔다.
    ...(totals !== undefined && { totals }),
});

/**
 * 파싱 결과를 처음 열 때 라인 컨테이너 칸을 맞춘다. B/L에 컨테이너가 없으면 대조할 기준이 없으므로 모든 칸을 비운다.
 * B/L에 컨테이너가 있고 PL에서 읽은 번호(라인·상단·하단)가 한 종류뿐이면 빈 칸에 채운다. B/L에 없는 번호여도 채워 라인 검증이 잡게 한다.
 * 여러 종류면 상단·하단 번호는 어느 라인 것인지 알 수 없어 버리고, 라인에서 읽은 번호만 그 라인에 둔다.
 */
const prefillContainers = (lines, documentNumbers, blContainerNumbers) => {
    if (blContainerNumbers.length === 0) return lines.map((line) => ({ ...line, cntr_hint: '' }));
    const numbers = [...documentNumbers, ...lines.map((line) => line.cntr_hint)].filter((n) => typeof n === 'string' && n.trim());
    if (new Set(numbers.map(normalizeContainerNo)).size !== 1) return lines;
    return lines.map((line) => (line.cntr_hint?.trim() ? line : { ...line, cntr_hint: numbers[0].trim() }));
};

/**
 * 저장된 편집본(또는 파서 결과)을 검수 화면의 헤더·라인으로 연다. 수량 목록이 없는 라인은 qty·qty_uom을 대표 수량으로 옮긴다.
 * 행 id는 열 때마다 새로 붙인다. 저장된 id를 그대로 쓰면 이번에 추가하는 행의 id와 겹칠 수 있다.
 * 아무도 저장하지 않은 파서 결과만 라인 컨테이너 칸을 채우고, 담당자가 저장한 편집본은 고치거나 비운 칸을 그대로 연다.
 */
export const fromDraftPayload = (payload, { blContainerNumbers = [], fromParser = false } = {}) => {
    const lines = (payload?.lines ?? []).map((line) => {
        const { id, ...rest } = renameKeys(line, invert(LINE_WIRE_KEYS));
        return ensureQuantities(newDraftLine(rest));
    });
    const documentNumbers = Array.isArray(payload?.container_numbers_in_doc) ? payload.container_numbers_in_doc : [];
    return {
        header: renameKeys(payload?.header, invert(HEADER_WIRE_KEYS)),
        lines: fromParser ? prefillContainers(lines, documentNumbers, blContainerNumbers) : lines,
        /** 문서 합계행. 파서가 읽은 그대로이고, 합계 대조 탭에서 보조 단위 수량만 고친다. 수기 문서는 null. */
        totals: payload?.totals && typeof payload.totals === 'object' ? payload.totals : null,
    };
};

/**
 * 원본 파일이 있는 문서인지. 수기 문서(새로 시작했거나 수기로 확정한 문서)는 원본이 없다.
 * 수기 새 문서가 처음 저장돼 id가 생겨도 원본은 없으므로 input_source로 가른다. 수정(edit) 작업은 자기 파일이 없으므로 확정 원본 유무로 본다.
 */
export const hasOriginalFile = (doc) => doc?.id != null && doc.input_source !== 'manual' && !(doc.input_source === 'edit' && doc.il_file_id_current == null);

/** 수기로 새로 시작하는 초안. 서버에는 아직 없고(id 없음), 처음 Save Draft·Confirm할 때 문서가 만들어진다. */
export const newManualDocument = () => ({ id: null, version: null, status: 'draft', payload: { header: { doc_type: 'PL', weight_uom: 'KG', volume_uom: 'CBM' }, lines: [] } });

/**
 * 문서 한 건(read/document) 응답을 검수 화면이 여는 모양으로 바꾼다.
 * 사용자가 저장한 편집본(draft_payload)이 우선이고, 아직 없으면 파서 원본(parsed_payload), 둘 다 없으면 빈 수기 초안으로 시작한다.
 * fromParser는 파서 원본을 연 것인지다. 파싱은 parsed_payload만 쓰고 draft_payload는 담당자가 저장해야 생긴다.
 */
export const fromDocument = (data) => ({
    ...data,
    fromParser: data.draft_payload == null && data.parsed_payload != null,
    payload: data.draft_payload ?? data.parsed_payload ?? { header: { doc_type: 'PL', weight_uom: 'KG', volume_uom: 'CBM' }, lines: [] },
});

/** 파서만 알 수 있는 문서 단위 판정. 셀 값으로 다시 계산하는 경고(신뢰도·CI/PL 번호·라인 없음)는 화면 검증이 맡는다. */
export const DOCUMENT_PARSE_WARNINGS = ['DOC_TYPE_OTHER', 'MULTIPLE_PL'];
/** 파서가 남긴 경고 중 문서 단위 판정만 돌려준다. 문서 종류가 아닌 것(DOC_TYPE_OTHER), 한 파일에 PL이 여럿(MULTIPLE_PL). */
export const parseWarningsOf = (doc) => (doc?.payload?.warnings ?? doc?.parsed_payload?.warnings ?? []).filter((w) => DOCUMENT_PARSE_WARNINGS.includes(w?.code));

/** 원문 위치를 담당자가 읽을 한 줄로(페이지 / 시트·행·셀). 없으면 null — 수기로 더한 행이다. */
export const describeSourceRef = (ref) => {
    if (!ref || typeof ref !== 'object') return null;
    const parts = [];
    if (ref.page) parts.push(`Page ${ref.page}`);
    if (ref.sheet) parts.push(`Sheet "${ref.sheet}"`);
    if (ref.rows?.length) parts.push(ref.rows.length === 1 ? `Row ${ref.rows[0]}` : `Rows ${Math.min(...ref.rows)}–${Math.max(...ref.rows)}`);
    if (ref.cells?.length) parts.push(`Cells ${ref.cells.join(', ')}`);
    return parts.length ? parts.join(' · ') : null;
};
